// Tasks screens. Pure: data in, HTML out.
// Words: TASKS_PAGE_WORDS.md. Mockup: MOCKUP_TASKS_APPROVED.html (approved 2026-10-06).
// Every change goes through server code (functions/src/tasks.ts).

import type { TasksData } from "./firebase";
import { REVIEW_LINE } from "./resources";
import {
  REASON_MAX,
  TASK_TYPES,
  TIERS,
  TIER_LINE,
  canSaveNewTask,
  changeLine,
  groupTasks,
  historyLines,
  likelyText,
  nextTaskId,
  pathwayText,
  shortDay,
  switchLine,
  type NewTaskInput,
  type Task,
  type TierGroup,
} from "./tasks";
import { esc, ident } from "./training-screens";

function row(label: string, value: string, end = ""): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e">${end}</div></div>`;
}

function sheet(title: string, body: string): string {
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="${esc(title)}">
    <div class="big">${esc(title)}</div>${body}</div></div>`;
}

const NONE = '<span class="muted">Not set</span>';
/** "Operate-close" on a button, kept on one line. */
const oneLine = (label: string) => label.replace(/-/g, "\u2011");
const LOCK = '<span class="lock">Locked</span>';

const TYPE_TONE: Record<string, string> = { Observe: "green", "Operate-close": "amber", Expose: "red" };

export function typePill(taskType: string, isActive: boolean): string {
  const tone = isActive ? (TYPE_TONE[taskType] ?? "grey") : "grey";
  return `<span class="pill ${tone}">${esc(taskType ? taskType.toUpperCase() : "NO TYPE")}</span>`;
}

function toggle(t: Task): string {
  return `<button class="tog${t.isActive ? " on" : ""}" type="button" role="switch" aria-checked="${t.isActive}" aria-label="${esc(t.name)} ${t.isActive ? "on" : "off"}" data-act="task-switch" data-id="${esc(t.taskId)}"></button>`;
}

// ---- 1. Manage menu line ----

export function tasksMenuLine(tasks: Task[]): string {
  return `${tasks.length} tasks · ${tasks.filter((t) => t.isActive).length} on`;
}

// ---- 2 and 3. The task list ----

export function tasksShell(d: TasksData, search: string, flash = ""): string {
  const on = d.tasks.filter((t) => t.isActive).length;
  const off = d.tasks.length - on;
  return `${ident("TK", "Tasks", `${d.tasks.length} tasks · ${on} on, ${off} off · the JSP task list reads these`)}
    ${flash}
    <div class="box"><div class="cnt">
      <div class="cnt-cell"><b>${on}</b><small>ON</small></div>
      <div class="cnt-cell"><b>${off}</b><small>OFF</small></div>
      <a class="cnt-cell tk-add" href="#/tasks/new"><b>+</b><small>ADD TASK</small></a>
    </div></div>
    <label class="search"><span class="sr">Search</span><input id="task-search" class="fin wide" type="search" value="${esc(search)}" placeholder="Search name or ID" autocomplete="off" autocapitalize="off" spellcheck="false"></label>
    <div id="task-groups"></div>`;
}

function taskRow(t: Task): string {
  return `<div class="tk-row${t.isActive ? "" : " dim"}">
    <a class="t" href="#/tasks/t/${encodeURIComponent(t.taskId)}"><b>${esc(t.name)}</b><span class="tk-tags"><span class="chip">${esc(t.taskId)}</span>${typePill(t.taskType, t.isActive)}</span></a>
    ${toggle(t)}
  </div>`;
}

export function taskGroups(groups: TierGroup[], search: string): string {
  const shown = groups.filter((g) => g.tasks.length);
  if (!shown.length) return `<div class="box"><div class="empty">No task matches "${esc(search)}".</div></div>`;
  return shown
    .map(
      (g) => `<div class="grp"><span>Tier ${g.tier} · ${esc(TIER_LINE[g.tier])}</span><span>${g.on} on · ${g.off} off</span></div>
      <div class="box">${g.tasks.map(taskRow).join("")}</div>`,
    )
    .join("");
}

export function groupsFor(d: TasksData, search: string): string {
  return taskGroups(groupTasks(d.tasks, search), search);
}

// ---- 4 and 5. On / Off confirm ----

export function switchSheet(t: Task, isOn: boolean, sopInLibrary: boolean, reason: string, busy: boolean, error = ""): string {
  const ready = Boolean(reason.trim()) && !busy;
  return sheet(
    `${isOn ? "Turn on" : "Turn off"} ${t.name}?`,
    `<p>${esc(switchLine(t, isOn, sopInLibrary))}</p>
    <div class="sheet-card"><label class="fl"><span class="lbl">Reason (required)</span><input id="task-reason" maxlength="${REASON_MAX}" value="${esc(reason)}" placeholder="${isOn ? "Why it is going on" : "Why it is going off"}" autocomplete="off"></label></div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go${isOn ? "" : " red"}" type="button" data-act="task-switch-confirm" data-needs-reason${ready ? "" : " disabled"}>${busy ? "Saving…" : isOn ? "Turn On" : "Turn Off"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>`,
  );
}

// ---- 6. Open a task ----

export function taskDetail(t: Task, d: TasksData, flash = ""): string {
  const id = encodeURIComponent(t.taskId);
  const sop = t.sopNumber ? d.sops.find((s) => s.sopNumber === t.sopNumber) : undefined;
  const sopValue = t.sopNumber
    ? `<span class="chip">${esc(t.sopNumber)}</span>${sop ? ` <small class="muted">${esc(sop.title)} · Rev ${esc(sop.version)}</small>` : ' <small class="warn-text">Not in the SOP Library yet</small>'}`
    : NONE;
  const lines = historyLines(d.changes[t.taskId] ?? [], d.edits[t.taskId] ?? []);
  const hist = lines.length
    ? lines
        .map((l) => `<div class="tk-hist"><b>${esc(shortDay(l.at))} · ${esc(l.text)}</b><small>${esc(l.who)}${l.reason ? ` · ${esc(l.reason)}` : ""}</small></div>`)
        .join("")
    : '<div class="empty">No changes since the copy from the Sheet.</div>';
  const offLine = t.isActive
    ? "Shows in the JSP task list."
    : `Off the JSP task list${t.lastDeactivatedOn ? ` since ${shortDay(t.lastDeactivatedOn)}` : ""}. QEW task currency does not run down while it is off.`;
  return `<a class="back" href="#/tasks">Tasks</a>
    ${ident(t.taskId.replace("TASK-", ""), t.name, `Tier ${Number.isFinite(t.tier) ? t.tier : "?"} · ${t.taskType || "No Task Type"}`)}
    ${flash}
    <div class="box">
      <div class="boxhd"><div class="nm"><b>${esc(t.name)}</b><small>ID and name are locked. JSP records and the SOP links use them.</small></div></div>
      <div class="rows">
        ${row("Task ID", `<span class="chip">${esc(t.taskId)}</span>`, LOCK)}
        ${row("Name", esc(t.name), LOCK)}
        ${row("Tier", `Tier ${esc(t.tier)} <small class="muted">${esc(TIER_LINE[t.tier as 1 | 2 | 3] ?? "")}</small>`, `<button class="chg" type="button" data-act="task-change" data-field="tier">Change</button>`)}
        ${row("Task Type", t.taskType ? typePill(t.taskType, true) : NONE, `<button class="chg" type="button" data-act="task-change" data-field="taskType">Change</button>`)}
        ${row("130.5(C) Likely", likelyText(t.likelihood1305C) ? esc(likelyText(t.likelihood1305C)) : NONE)}
        ${row("Authorization", pathwayText(t.authorizationPathway) ? esc(pathwayText(t.authorizationPathway)) : NONE)}
        ${row("SOP", sopValue, sop ? `<a class="chg" href="#/tasks/t/${id}/sop">Open SOP</a>` : "")}
        ${row("JSP Task List", `<span class="pill ${t.isActive ? "green" : "grey"}">${t.isActive ? "ON" : "OFF"}</span> <small class="muted">${esc(offLine)}</small>`, toggle(t))}
      </div>
    </div>
    <div class="box">
      <div class="boxhd"><div class="nm"><b>History</b><small>Each change, with who and why</small></div></div>
      ${hist}
    </div>`;
}

// ---- 7. Tier or Task Type change ----

export interface ChangeEntry {
  field: "tier" | "taskType";
  to: string;
  reason: string;
}

export function changeSheet(t: Task, e: ChangeEntry, busy: boolean, error = ""): string {
  const isTierField = e.field === "tier";
  const options = isTierField ? TIERS.map((n) => ({ value: String(n), label: `Tier ${n}` })) : TASK_TYPES.map((x) => ({ value: x, label: oneLine(x) }));
  const current = isTierField ? String(t.tier) : t.taskType;
  const seg = options
    .map(
      (o) => `<label class="seg-opt${o.value === current ? " now" : ""}"><input type="radio" name="task-to" value="${esc(o.value)}"${o.value === e.to ? " checked" : ""}><span>${esc(o.label)}${o.value === current ? "<small>now</small>" : ""}</span></label>`,
    )
    .join("");
  const picked = e.to && e.to !== current;
  const line = picked ? changeLine(t, e.field, isTierField ? Number(e.to) : e.to) : isTierField ? "The tier sets the JSP rail. Pick the new tier." : "The risk engine scores by Task Type. Pick the new type.";
  const ready = picked && Boolean(e.reason.trim()) && !busy;
  const what = isTierField ? "Tier" : "Task Type";
  return sheet(
    `Change the ${what} of ${t.name}?`,
    `<div class="seg" role="radiogroup" aria-label="${esc(what)}">${seg}</div>
    <p id="task-change-line">${esc(line)}</p>
    <div class="sheet-card"><label class="fl"><span class="lbl">Reason (required)</span><input id="task-reason" maxlength="${REASON_MAX}" value="${esc(e.reason)}" placeholder="Why the ${isTierField ? "tier" : "type"} is changing" autocomplete="off"></label></div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go" type="button" data-act="task-change-confirm" data-needs-reason data-needs-pick${ready ? "" : " disabled"}>${busy ? "Saving…" : `Change ${what}`}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>`,
  );
}

// ---- 8 and 9. Add Task ----

export function addTaskForm(d: TasksData, entry: NewTaskInput, busy: boolean, error = ""): string {
  const nextId = nextTaskId(d.tasks.map((t) => t.taskId));
  const seg = (name: string, opts: { value: string; label: string }[], picked: string) =>
    `<div class="seg" role="radiogroup">${opts
      .map((o) => `<label class="seg-opt"><input type="radio" name="${name}" value="${esc(o.value)}"${o.value === picked ? " checked" : ""}><span>${esc(o.label)}</span></label>`)
      .join("")}</div>`;
  const ready = canSaveNewTask(entry) && !busy;
  return `<a class="back" href="#/tasks">Cancel</a>
    ${ident("+", "Add Task", "For a task a company asks for")}
    <form class="box" id="task-form" novalidate>
      <div class="boxhd"><div class="nm"><b>New Task</b><small>The next free ID fills in.</small></div><span class="chip">${esc(nextId)}</span></div>
      <div class="rows">
        <label class="r"><span class="k">Name</span><span class="v"><input class="fin wide" name="name" value="${esc(entry.name)}" maxlength="60" placeholder="e.g. Battery Charger Inspection" autocomplete="off" required></span><span class="e"></span></label>
        <div class="r"><span class="k">Tier (required)</span><span class="v">${seg("tier", TIERS.map((n) => ({ value: String(n), label: `Tier ${n}` })), entry.tier === null ? "" : String(entry.tier))}</span><span class="e"></span></div>
        <div class="r"><span class="k">Task Type (required)</span><span class="v">${seg("taskType", TASK_TYPES.map((x) => ({ value: x, label: oneLine(x) })), entry.taskType)}</span><span class="e"></span></div>
        <label class="r"><span class="k">SOP Number</span><span class="v"><input class="fin wide" name="sopNumber" value="${esc(entry.sopNumber)}" maxlength="20" placeholder="e.g. SOP-28.47" autocomplete="off" autocapitalize="characters"></span><span class="e"></span></label>
      </div>
    </form>
    <div class="note" id="task-form-note">${ready ? "Saves switched Off. Turn it on when its SOP page is ready." : "Tier and Task Type have no default. Save stays off until name, tier and Task Type are all filled in."}</div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="task-form" id="task-save"${ready ? "" : " disabled"}>${busy ? "Saving…" : "Save"}</button>`;
}

// ---- Open SOP (the Resources review view) ----

export function taskSopView(t: Task, sopTitle: string, frameUrl: string | null, error = ""): string {
  const back = `<a class="back" href="#/tasks/t/${encodeURIComponent(t.taskId)}">${esc(t.name)}</a>`;
  const body = error
    ? `<div class="note red">${esc(error)}</div><button class="prim" type="button" data-act="task-sop-retry">Try Again</button>`
    : frameUrl
      ? `<div class="res-review">${esc(REVIEW_LINE)}</div><iframe class="res-frame tk-frame" title="${esc(t.sopNumber)}" src="${esc(frameUrl)}"></iframe>`
      : `<div class="loading">Opening ${esc(t.sopNumber)}…</div>`;
  return `${back}${ident("SOP", sopTitle, `${t.sopNumber} · review only, as the QEW sees it in Resources`)}${body}`;
}
